import { Horarios } from '../constant/HoursConstant';

/**
 * FormatDate
 *
 * Esta classe contém métodos utilitários para lidar com formatação de datas e horários.
 */
export const FormatDate = {
  /**
   * Formata uma data e um horário no formato ISO 8601 (YYYY-MM-DDTHH:mm:ss).
   *
   * @param dateAppointment - Data no formato 'YYYY-MM-DD'.
   * @param time - Horário no formato 'HH:mm'.
   * @returns A data e o horário formatados como string no formato ISO 8601.
   */
  formatDateTime(dateAppointment: string, time: string): string {
    if (!dateAppointment || !time) {
      throw new Error('A data e o horário devem ser fornecidos.');
    }

    if (!/^\d{2}:\d{2}$/.test(time)) {
      throw new Error('O horário deve estar no formato HH:mm.');
    }

    const [hours, minutes] = time.split(':');
    return `${dateAppointment}T${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}:00`;
  },

  /**
   * Divide uma string de data e horário no formato 'YYYY-MM-DDTHH:mm:ss'
   * e retorna um objeto contendo a data e o horário separados.
   *
   * @param dateTime - Data e horário no formato 'YYYY-MM-DDTHH:mm:ss'.
   * @returns Um objeto com a data e o horário separados.
   */
  splitDateTime(dateTime: string): { date: string; time: string } {
    if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/.test(dateTime)) {
      throw new Error('A data e o horário devem estar no formato YYYY-MM-DDTHH:mm:ss.');
    }

    const [date, timeWithSeconds] = dateTime.split('T');
    const time = timeWithSeconds.substring(0, 5);

    return { date, time };
  },

  /**
   * Formata uma string de data e horário para o formato 'YYYY-MM-DD'.
   * Aceita datas no formato 'dd/MM/yyyy' ou em qualquer formato reconhecível por JavaScript.
   *
   * @param dateTime - Data no formato 'dd/MM/yyyy' ou em um formato ISO reconhecível por JavaScript.
   * @returns Uma string contendo apenas a data no formato 'YYYY-MM-DD'.
   */
  formatToDateOnly(dateTime: string): string {
    // Verifica se a data está no formato 'dd/MM/yyyy'
    const ddMmYyyyPattern = /^\d{1,2}\/\d{1,2}\/\d{4}$/;

    let date: Date;

    if (ddMmYyyyPattern.test(dateTime)) {
      // Converte o formato 'dd/MM/yyyy' para uma instância de Date
      const [day, month, year] = dateTime.split('/');
      date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day)); // Mês no JavaScript vai de 0 a 11
    } else {
      // Tenta criar a data a partir de um formato ISO ou reconhecido pelo JavaScript
      date = new Date(dateTime);
    }

    // Valida se a data gerada é válida
    if (isNaN(date.getTime())) {
      throw new Error('A string fornecida não é uma data válida.');
    }

    // Formata a data no padrão 'YYYY-MM-DD'
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  },

  /**
   * Formata uma data e horário, encontrando o próximo horário disponível em um intervalo de 15 minutos,
   * e retorna o horário de início e de término, juntamente com a data.
   *
   * @param dateTime - Data ou data e horário no formato 'YYYY-MM-DD' ou 'YYYY-MM-DDTHH:mm:ss'.
   * @returns Um objeto contendo a data, horário de início e horário de término (15 minutos depois).
   */
  formatDateWith15MinEnd(dateTime: string): { date: string; start: string; end: string } {
    let date: Date;

    if (/^\d{4}-\d{2}-\d{2}$/.test(dateTime)) {
      date = new Date(dateTime);
      const now = new Date();
      date.setHours(now.getHours(), now.getMinutes(), now.getSeconds(), 0);
    } else {
      date = new Date(dateTime);
      if (isNaN(date.getTime())) {
        throw new Error('A string fornecida não é uma data válida.');
      }
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const formattedDate = `${year}-${month}-${day}`;

    const currentHours = String(date.getHours()).padStart(2, '0');
    const currentMinutes = String(date.getMinutes()).padStart(2, '0');
    const currentTime = `${currentHours}:${currentMinutes}`;

    const nextStartIndex = Horarios.findIndex((h) => h > currentTime);
    const start = nextStartIndex !== -1 ? Horarios[nextStartIndex] : Horarios[0];

    const startHour = parseInt(start.split(':')[0], 10);
    const startMinute = parseInt(start.split(':')[1], 10);
    const startDate = new Date(date);
    startDate.setHours(startHour, startMinute);

    const endDate = new Date(startDate);
    endDate.setMinutes(endDate.getMinutes() + 15);

    const end =
      Horarios.find((h) => h >= `${endDate.getHours().toString().padStart(2, '0')}:${endDate.getMinutes().toString().padStart(2, '0')}`) || Horarios[0];

    return {
      date: formattedDate,
      start: start,
      end: end,
    };
  },

  /**
   * Converte uma string de data no formato ISO para o formato brasileiro 'dd/MM/yyyy'.
   *
   * @param dateTime - String de data no formato ISO (YYYY-MM-DDTHH:mm:ss).
   * @returns Data formatada no padrão brasileiro (dd/MM/yyyy).
   */
  formatToBrazilianDate(dateTime: string): string {
    const date = new Date(dateTime);
    if (isNaN(date.getTime())) {
      throw new Error('A string fornecida não é uma data válida.');
    }

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
  },

  /**
   * Converte uma data de entrada em formato 'MM/dd/yyyy' ou 'yyyy-MM-dd' para o formato ISO 8601.
   *
   * @param dateInput - Data no formato 'MM/dd/yyyy' ou 'yyyy-MM-dd'.
   * @returns String de data no formato ISO 8601 (YYYY-MM-DDTHH:mm:ss).
   */
  formatToIsoDate(dateInput: string): string {
    const mmDdYyyyPattern = /^\d{2}\/\d{2}\/\d{4}$/;
    const yyyyMmDdPattern = /^\d{4}-\d{2}-\d{2}$/;

    let date: Date;

    if (mmDdYyyyPattern.test(dateInput)) {
      const [month, day, year] = dateInput.split('/');
      date = new Date(`${year}-${month}-${day}T00:00:00`);
    } else if (yyyyMmDdPattern.test(dateInput)) {
      date = new Date(`${dateInput}T00:00:00`);
    } else {
      throw new Error('O formato de data deve ser MM/dd/yyyy ou yyyy-MM-dd.');
    }

    if (isNaN(date.getTime())) {
      throw new Error('A string fornecida não é uma data válida.');
    }

    return date.toISOString().split('.')[0];
  },

  /**
   * Formata a data no formato 'yyyy-MM-dd' para passar na validação em C#.
   * Aceita datas nos formatos 'MM/dd/yyyy' ou 'yyyy-MM-dd' como entrada.
   *
   * @param dateInput - Data no formato 'MM/dd/yyyy' ou 'yyyy-MM-dd'.
   * @returns Uma string formatada no padrão 'yyyy-MM-dd'.
   */
  formatForValidation(dateInput: string): string {
    const mmDdYyyyPattern = /^\d{2}\/\d{2}\/\d{4}$/;
    const yyyyMmDdPattern = /^\d{4}-\d{2}-\d{2}$/;

    let date: Date;

    if (mmDdYyyyPattern.test(dateInput)) {
      const [month, day, year] = dateInput.split('/');
      date = new Date(Date.UTC(parseInt(year), parseInt(month) - 1, parseInt(day)));
    } else if (yyyyMmDdPattern.test(dateInput)) {
      date = new Date(dateInput + 'T00:00:00Z'); // Adiciona o 'Z' para tratar como UTC
    } else {
      throw new Error('O formato de data deve ser MM/dd/yyyy ou yyyy-MM-dd.');
    }

    if (isNaN(date.getTime())) {
      throw new Error('A string fornecida não é uma data válida.');
    }

    const year = date.getUTCFullYear(); // Usa getUTCFullYear para obter o ano em UTC
    const month = String(date.getUTCMonth() + 1).padStart(2, '0'); // Usa getUTCMonth
    const day = String(date.getUTCDate()).padStart(2, '0'); // Usa getUTCDate

    return `${year}-${month}-${day}`;
  },
};
